import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {BLUR_FOR_LIGHT_GLOW, BLUR_FOR_STRONG_GLOW, ITEM_TYPE, SHADOW_REFERENCE_DIMENSION} from '@PosterWhiteboard/items/item/item.types';
import type {RGBA} from '@Utils/color.util';
import {colorToRgbaArray, rgbaToHexString} from '@Utils/color.util';
import type {SerializedShadowOptions} from '@postermywall/fabricjs-2';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';
import {degreesToRadians, radiansToDegrees} from '@Utils/math.util';
import {GlowType, type NonAudioItemBackendObject, ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {DEFAULT_TEXT_SHADOW_BLUR, DEFAULT_TEXT_SHADOW_DISTANCE} from '@PosterWhiteboard/items/text-item/text-item.types';

const DEFAULT_SHADOW_DISTANCE = 15;
const DEFAULT_SHADOW_BLUR = 5;

export interface ShadowOrGlowOptionsObject {
  distance: number;
  blur: number;
  color: RGBA;
  angle: number;
  shadowOrGlowType: ShadowOrGlowType;
}

export const getDefaultBlurForItemType = (itemType: ITEM_TYPE): number => {
  switch (itemType) {
    case ITEM_TYPE.TEXT:
    case ITEM_TYPE.TEXTSLIDE:
      return DEFAULT_TEXT_SHADOW_BLUR;
    default:
      return DEFAULT_SHADOW_BLUR;
  }
};

export const getDefaultDistanceForItemType = (itemType: ITEM_TYPE): number => {
  switch (itemType) {
    case ITEM_TYPE.TEXT:
    case ITEM_TYPE.TEXTSLIDE:
      return DEFAULT_TEXT_SHADOW_DISTANCE;
    default:
      return DEFAULT_SHADOW_DISTANCE;
  }
};

export const getDefaultGlowObjectFromGlowType = (type: GlowType | null): ShadowOrGlowOptionsObject | null => {
  if (type === null) {
    return null;
  }

  switch (type) {
    case GlowType.LIGHT:
    case GlowType.STRONG:
      return {
        shadowOrGlowType: type === GlowType.STRONG ? ShadowOrGlowType.STRONG_GLOW : ShadowOrGlowType.LIGHT_GLOW,
        blur: type === GlowType.STRONG ? BLUR_FOR_STRONG_GLOW : BLUR_FOR_LIGHT_GLOW,
        distance: 0,
        angle: 0,
        color: [255, 255, 255, 1],
      };
    case GlowType.NONE:
      return null;
    default:
      throw new Error(`Unhandled glow type ${type} in getDefaultGlowObjectFromGlowType`);
  }
};

export const getDefaultShadowObjectFromShadowType = (type: ShadowType | null, itemType: ITEM_TYPE): ShadowOrGlowOptionsObject | null => {
  if (type === null) {
    return null;
  }

  switch (type) {
    case ShadowType.CUSTOM:
      return {
        shadowOrGlowType: ShadowOrGlowType.CUSTOM_SHADOW,
        distance: getDefaultDistanceForItemType(itemType),
        angle: 45,
        blur: getDefaultBlurForItemType(itemType),
        color: [0, 0, 0, 0.5],
      };
    case ShadowType.STRONG:
      return {
        shadowOrGlowType: ShadowOrGlowType.STRONG_SHADOW,
        angle: 45,
        blur: getDefaultBlurForItemType(itemType),
        distance: getDefaultDistanceForItemType(itemType),
        color: [0, 0, 0, 0.5],
      };
    case ShadowType.LIGHT:
      return {
        shadowOrGlowType: ShadowOrGlowType.LIGHT_SHADOW,
        distance: getDefaultDistanceForItemType(itemType),
        angle: 45,
        blur: getDefaultBlurForItemType(itemType),
        color: [0, 0, 0, 0.25],
      };
    case ShadowType.NONE:
      return null;
    default:
      throw new Error('getDefaultShadowObjectFromShadowType only supports shadow types.');
  }
};

export const convertShadowOptionsBlurToFabricShadowBlur = (shadowOptionsBlur: number, shadowScale: number): number => {
  return shadowOptionsBlur * shadowScale;
};

export const getFabricShadowOffsetsFromShadowOptions = (shadowOptions: ShadowOrGlowOptionsObject, shadowScale: number): Pick<SerializedShadowOptions, 'offsetX' | 'offsetY'> => {
  const dx = shadowOptions.distance * Math.cos(degreesToRadians(shadowOptions.angle));
  const dy = shadowOptions.distance * Math.sin(degreesToRadians(shadowOptions.angle));

  return {
    offsetX: dx * shadowScale,
    offsetY: dy * shadowScale,
  };
};

export const convertShadowOptionsObjectToFabricShadowObject = (shadowOptions: ShadowOrGlowOptionsObject | null, shadowScale: number): SerializedShadowOptions | null => {
  if (!shadowOptions) {
    return null;
  }

  if (shadowOptions.shadowOrGlowType === ShadowOrGlowType.LIGHT_GLOW || shadowOptions.shadowOrGlowType === ShadowOrGlowType.STRONG_GLOW) {
    const alpha = shadowOptions.shadowOrGlowType === ShadowOrGlowType.LIGHT_GLOW ? 0.8 : 1;
    const co = rgbaToHexString([shadowOptions.color[0], shadowOptions.color[1], shadowOptions.color[2], alpha]);

    return {
      nonScaling: false,
      type: 'shadow',
      affectStroke: false,
      color: co,
      shadowOrGlowType: shadowOptions.shadowOrGlowType,
      offsetX: 0,
      offsetY: 0,
      blur: shadowOptions.blur,
    };
  }

  const colorHex = rgbaToHexString(shadowOptions.color);
  const dx = shadowOptions.distance * Math.cos(degreesToRadians(shadowOptions.angle));
  const dy = shadowOptions.distance * Math.sin(degreesToRadians(shadowOptions.angle));

  return {
    nonScaling: false,
    type: 'shadow',
    color: colorHex,
    blur: shadowOptions.blur * shadowScale,
    offsetX: dx * shadowScale,
    offsetY: dy * shadowScale,
    affectStroke: true,
    shadowOrGlowType: shadowOptions.shadowOrGlowType,
  };
};

export const convertFabricShadowObjectToShadowOrGlowOptionsObject = (shadow: SerializedShadowOptions | null, shadowScale: number): ShadowOrGlowOptionsObject | null => {
  if (!shadow) {
    return null;
  }

  const blur = shadow.blur / shadowScale;
  const offsetX = shadow.offsetX / shadowScale;
  const offsetY = shadow.offsetY / shadowScale;

  const distance = Math.sqrt(offsetX ** 2 + offsetY ** 2);
  const angleRadians = Math.atan2(offsetY, offsetX);
  const angle = radiansToDegrees(angleRadians);
  const normalizedAngle = (angle + 360) % 360;

  const color = colorToRgbaArray(shadow.color);

  return {
    shadowOrGlowType: shadow.shadowOrGlowType,
    distance: distance,
    color,
    angle: normalizedAngle,
    blur,
  };
};

export const getScaleForShadowFromItemObject = (itemObject: ItemObject | NonAudioItemBackendObject): number => {
  if (itemObject.gitype === ITEM_TYPE.TEXT || itemObject.gitype === ITEM_TYPE.TEXTSLIDE || itemObject.gitype === ITEM_TYPE.TRANSCRIPT) {
    return 1;
  }

  const maxDimension = Math.max(itemObject.height, itemObject.width);
  return maxDimension / SHADOW_REFERENCE_DIMENSION;
};

export const getFabricShadowOrGlowTypeFromGlowType = (glowType: GlowType | null): ShadowOrGlowType | null => {
  if (glowType === null) {
    return null;
  }

  switch (glowType) {
    case GlowType.STRONG:
      return ShadowOrGlowType.STRONG_GLOW;
    case GlowType.LIGHT:
      return ShadowOrGlowType.LIGHT_GLOW;
    case GlowType.NONE:
      return null;
  }
};

export const getFabricShadowOrGlowTypeFromShadowType = (shadowType: ShadowType | null): ShadowOrGlowType | null => {
  if (shadowType === null) {
    return null;
  }

  switch (shadowType) {
    case ShadowType.STRONG:
      return ShadowOrGlowType.STRONG_SHADOW;
    case ShadowType.LIGHT:
      return ShadowOrGlowType.LIGHT_SHADOW;
    case ShadowType.CUSTOM:
      return ShadowOrGlowType.CUSTOM_SHADOW;
    case ShadowType.NONE:
      return null;
  }
};

export const getShadowTypeFromFabricShadowOrGlowType = (shadowOrGlowType: ShadowOrGlowType | null): ShadowType => {
  if (!shadowOrGlowType) {
    return ShadowType.NONE;
  }

  switch (shadowOrGlowType) {
    case ShadowOrGlowType.STRONG_SHADOW:
      return ShadowType.STRONG;
    case ShadowOrGlowType.LIGHT_SHADOW:
      return ShadowType.LIGHT;
    case ShadowOrGlowType.CUSTOM_SHADOW:
      return ShadowType.CUSTOM;
    case ShadowOrGlowType.LIGHT_GLOW:
    case ShadowOrGlowType.STRONG_GLOW:
    default:
      return ShadowType.NONE;
  }
};

export const getGlowTypeFromFabricShadowOrGlowType = (shadowOrGlowType: ShadowOrGlowType | null): GlowType => {
  if (!shadowOrGlowType) {
    return GlowType.NONE;
  }

  switch (shadowOrGlowType) {
    case ShadowOrGlowType.LIGHT_GLOW:
      return GlowType.LIGHT;
    case ShadowOrGlowType.STRONG_GLOW:
      return GlowType.STRONG;
    default:
      return GlowType.NONE;
  }
};

export const isCustomShadow = (shadowOrGlowtype?: ShadowOrGlowType | null): boolean => {
  return shadowOrGlowtype === ShadowOrGlowType.CUSTOM_SHADOW;
};

export const isShadow = (shadowOrGlowtype?: ShadowOrGlowType | null): boolean => {
  return shadowOrGlowtype === ShadowOrGlowType.CUSTOM_SHADOW || shadowOrGlowtype === ShadowOrGlowType.LIGHT_SHADOW || shadowOrGlowtype === ShadowOrGlowType.STRONG_SHADOW;
};

export const isGlow = (shadowOrGlowtype?: ShadowOrGlowType | null): boolean => {
  return shadowOrGlowtype === ShadowOrGlowType.LIGHT_GLOW || shadowOrGlowtype === ShadowOrGlowType.STRONG_GLOW;
};

export const getDefaultFabricShadowForType = (shadowOrGlowtype: ShadowOrGlowType | null, itemType: ITEM_TYPE, shadowScale: number): SerializedShadowOptions | null => {
  if (isGlow(shadowOrGlowtype)) {
    return convertShadowOptionsObjectToFabricShadowObject(getDefaultGlowObjectFromGlowType(getGlowTypeFromFabricShadowOrGlowType(shadowOrGlowtype)), shadowScale);
  }

  if (isShadow(shadowOrGlowtype)) {
    return convertShadowOptionsObjectToFabricShadowObject(getDefaultShadowObjectFromShadowType(getShadowTypeFromFabricShadowOrGlowType(shadowOrGlowtype), itemType), shadowScale);
  }

  return null;
};
